import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

type AccessState = {
  loading: boolean;
  graceActive: boolean;
  graceUntil: string | null;
  delinquentSince: string | null;
  locked: boolean; // beyond grace period → restrict to Billing + read-only
  trialEnd: string | null;
  trialStatus: 'active' | 'ending_soon' | 'ending_today' | 'expired' | null;
  trialHoursLeft: number | null;
};

export function useBillingAccess(): AccessState {
  const [state, setState] = useState<AccessState>({
    loading: true,
    graceActive: false,
    graceUntil: null,
    delinquentSince: null,
    locked: false,
    trialEnd: null,
    trialStatus: null,
    trialHoursLeft: null,
  });

  useEffect(() => {
    (async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        setState({
          loading: false,
          graceActive: false,
          graceUntil: null,
          delinquentSince: null,
          locked: false,
          trialEnd: null,
          trialStatus: null,
          trialHoursLeft: null,
        });
        return;
      }
      const { data: userRow } = await supabase
        .from('users')
        .select('tenant_id')
        .eq('id', session.user.id)
        .single();
      if (!userRow?.tenant_id) {
        setState({
          loading: false,
          graceActive: false,
          graceUntil: null,
          delinquentSince: null,
          locked: false,
          trialEnd: null,
          trialStatus: null,
          trialHoursLeft: null,
        });
        return;
      }
      const { data: sub } = await supabase
        .from('tenant_subscriptions')
        .select('grace_until, delinquent_since, trial_end')
        .eq('tenant_id', userRow.tenant_id)
        .maybeSingle();
      const graceUntil = sub?.grace_until as string | null;
      const delinquentSince = sub?.delinquent_since as string | null;
      const trialEnd = sub?.trial_end as string | null;

      const now = Date.now();
      const active = !!graceUntil && new Date(graceUntil).getTime() > now;
      const locked = !!graceUntil && new Date(graceUntil).getTime() <= now;

      // Calculate trial status
      let trialStatus: 'active' | 'ending_soon' | 'ending_today' | 'expired' | null = null;
      let trialHoursLeft: number | null = null;

      if (trialEnd) {
        const trialEndTime = new Date(trialEnd).getTime();
        trialHoursLeft = Math.max(0, Math.floor((trialEndTime - now) / (1000 * 60 * 60)));

        if (trialHoursLeft <= 0) {
          trialStatus = 'expired';
        } else if (trialHoursLeft <= 2) {
          trialStatus = 'ending_today';
        } else if (trialHoursLeft <= 24) {
          trialStatus = 'ending_today';
        } else if (trialHoursLeft <= 72) {
          trialStatus = 'ending_soon';
        } else {
          trialStatus = 'active';
        }
      }

      setState({
        loading: false,
        graceActive: active,
        graceUntil: graceUntil || null,
        delinquentSince: delinquentSince || null,
        locked,
        trialEnd: trialEnd || null,
        trialStatus,
        trialHoursLeft,
      });
    })();
  }, []);

  return state;
}
